/**
 * Step5Verification
 * Verification and compliance: ISED Business Number, provincial license,
 * BBB accreditation, industry certifications, insurance, document uploads,
 * privacy policy & terms of service URLs.
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import FileUpload from '../../components/FileUpload';

export function validateStep5(data) {
  const errors = {};
  if (data.isedBusinessNumber && !/^\d{6,10}$/.test(data.isedBusinessNumber.replace(/[\s-]/g, ''))) {
    errors.isedBusinessNumber = 'ISED Business Number must be 6-10 digits';
  }
  if (data.privacyPolicyUrl && !/^https?:\/\/.+/.test(data.privacyPolicyUrl)) {
    errors.privacyPolicyUrl = 'Please enter a valid URL starting with http:// or https://';
  }
  if (data.termsUrl && !/^https?:\/\/.+/.test(data.termsUrl)) {
    errors.termsUrl = 'Please enter a valid URL starting with http:// or https://';
  }
  return errors;
}

export default function Step5Verification({ data, onChange, errors, touched, onBlur }) {
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    onChange({ ...data, [name]: type === 'checkbox' ? checked : value });
  }, [data, onChange]);

  const handleCertificationAdd = useCallback(() => {
    const certs = data.certifications || [];
    onChange({ ...data, certifications: [...certs, { name: '', issuedBy: '', year: '' }] });
  }, [data, onChange]);

  const handleCertificationChange = useCallback((index, field, value) => {
    const certs = [...(data.certifications || [])];
    certs[index] = { ...certs[index], [field]: value };
    onChange({ ...data, certifications: certs });
  }, [data, onChange]);

  const handleCertificationRemove = useCallback((index) => {
    const certs = (data.certifications || []).filter((_, i) => i !== index);
    onChange({ ...data, certifications: certs });
  }, [data, onChange]);

  const handleFileChange = useCallback((field, file) => {
    onChange({ ...data, [field]: file });
  }, [data, onChange]);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={sectionTitleStyle}>Verification & Compliance</h2>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Provide verification details to build trust with potential customers.
          Verified providers receive a trust badge on their listing.
        </p>
      </div>

      {/* ISED Business Number */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>Business Registration</h3>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 240px' }}>
            <label style={labelStyle}>ISED Business Number</label>
            <input
              type="text"
              name="isedBusinessNumber"
              value={data.isedBusinessNumber || ''}
              onChange={handleChange}
              onBlur={() => onBlur('isedBusinessNumber')}
              style={{ ...inputStyle, borderColor: touched.isedBusinessNumber && errors.isedBusinessNumber ? '#ef4444' : '#d1d5db' }}
              placeholder="e.g., 1234567"
            />
            {touched.isedBusinessNumber && errors.isedBusinessNumber && <div style={errorStyle}>{errors.isedBusinessNumber}</div>}
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
              Innovation, Science and Economic Development Canada registration number
            </div>
          </div>
          <div style={{ flex: '1 1 240px' }}>
            <label style={labelStyle}>Provincial License Number</label>
            <input
              type="text"
              name="provincialLicense"
              value={data.provincialLicense || ''}
              onChange={handleChange}
              style={inputStyle}
              placeholder="License number (if applicable)"
            />
          </div>
        </div>
      </section>

      {/* BBB Accreditation */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>BBB Accreditation</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
              <input
                type="checkbox"
                name="bbbAccredited"
                checked={data.bbbAccredited || false}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                BBB Accredited Business
              </span>
            </label>
          </div>
          {data.bbbAccredited && (
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>BBB Accreditation ID</label>
              <input
                type="text"
                name="bbbId"
                value={data.bbbId || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="BBB ID number"
              />
            </div>
          )}
        </div>
      </section>

      {/* Industry Certifications */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>Industry Certifications</h3>
        {(data.certifications || []).length === 0 && (
          <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '12px' }}>
            No certifications added yet.
          </p>
        )}
        {(data.certifications || []).map((cert, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '12px',
              alignItems: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 180px' }}>
              <label style={labelStyle}>Certification Name</label>
              <input
                type="text"
                value={cert.name}
                onChange={(e) => handleCertificationChange(index, 'name', e.target.value)}
                style={inputStyle}
                placeholder="e.g., ISO 27001"
              />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <label style={labelStyle}>Issued By</label>
              <input
                type="text"
                value={cert.issuedBy}
                onChange={(e) => handleCertificationChange(index, 'issuedBy', e.target.value)}
                style={inputStyle}
                placeholder="Issuing organization"
              />
            </div>
            <div style={{ flex: '0 1 100px' }}>
              <label style={labelStyle}>Year</label>
              <input
                type="text"
                value={cert.year}
                onChange={(e) => handleCertificationChange(index, 'year', e.target.value)}
                style={inputStyle}
                placeholder="2024"
              />
            </div>
            <button
              type="button"
              onClick={() => handleCertificationRemove(index)}
              style={{
                padding: '10px 14px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#ef4444',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={handleCertificationAdd}
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#3b82f6',
            backgroundColor: '#f0f9ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          + Add Certification
        </button>
      </section>

      {/* Insurance */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>Insurance Coverage</h3>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          marginBottom: '16px',
        }}>
          <input
            type="checkbox"
            name="hasInsurance"
            checked={data.hasInsurance || false}
            onChange={handleChange}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
            Business has active insurance coverage
          </span>
        </label>
        {data.hasInsurance && (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Insurance Provider</label>
              <input
                type="text"
                name="insuranceProvider"
                value={data.insuranceProvider || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Insurance company name"
              />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Policy Number</label>
              <input
                type="text"
                name="insurancePolicyNumber"
                value={data.insurancePolicyNumber || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Policy #"
              />
            </div>
          </div>
        )}
      </section>

      {/* Document uploads */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>Document Uploads</h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 280px' }}>
            <FileUpload
              label="Business License"
              accept="all"
              file={data.businessLicenseFile || null}
              onChange={(file) => handleFileChange('businessLicenseFile', file)}
              helperText="Upload a copy of your business license (PDF or image)"
            />
          </div>
          <div style={{ flex: '1 1 280px' }}>
            <FileUpload
              label="Insurance Certificate"
              accept="all"
              file={data.insuranceCertFile || null}
              onChange={(file) => handleFileChange('insuranceCertFile', file)}
              helperText="Upload your certificate of insurance (PDF or image)"
            />
          </div>
        </div>
      </section>

      {/* Policy URLs */}
      <section style={{ ...sectionStyle, marginBottom: 0 }}>
        <h3 style={subTitleStyle}>Policies</h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 280px' }}>
            <label style={labelStyle}>Privacy Policy URL</label>
            <input
              type="url"
              name="privacyPolicyUrl"
              value={data.privacyPolicyUrl || ''}
              onChange={handleChange}
              onBlur={() => onBlur('privacyPolicyUrl')}
              style={{ ...inputStyle, borderColor: touched.privacyPolicyUrl && errors.privacyPolicyUrl ? '#ef4444' : '#d1d5db' }}
              placeholder="https://company.ca/privacy"
            />
            {touched.privacyPolicyUrl && errors.privacyPolicyUrl && <div style={errorStyle}>{errors.privacyPolicyUrl}</div>}
          </div>
          <div style={{ flex: '1 1 280px' }}>
            <label style={labelStyle}>Terms of Service URL</label>
            <input
              type="url"
              name="termsUrl"
              value={data.termsUrl || ''}
              onChange={handleChange}
              onBlur={() => onBlur('termsUrl')}
              style={{ ...inputStyle, borderColor: touched.termsUrl && errors.termsUrl ? '#ef4444' : '#d1d5db' }}
              placeholder="https://company.ca/terms"
            />
            {touched.termsUrl && errors.termsUrl && <div style={errorStyle}>{errors.termsUrl}</div>}
          </div>
        </div>
      </section>
    </div>
  );
}

Step5Verification.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  onBlur: PropTypes.func.isRequired,
};

const sectionTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '6px',
};

const subTitleStyle = {
  fontSize: '15px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '12px',
};

const sectionStyle = {
  backgroundColor: '#f8fafc',
  borderRadius: '10px',
  padding: '20px',
  marginBottom: '20px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle = {
  fontSize: '12px',
  color: '#ef4444',
  marginTop: '3px',
};
